import { type ReactNode, useCallback, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { PlainInputW26, PlainInputW34, PlainInputW58 } from 'app/common/ui/PlainInput'
import PlainInput from 'app/common/ui-next/plain-input'
import format from 'app/common/constants/identifiers/formats'
import type { IdentifierType } from 'app/common/constants/identifiers'
import PlainLabel from 'app/common/ui-next/plain-label'
import * as identifierTypes from 'app/common/constants/identifiers/types'
import PlainLink from 'app/common/ui-next/plain-link'

import IconByTypeWithMarks from '../identifiers-icons/icon-by-type-with-marks'
import styles from './identifier-form-entering-number.module.scss'

type Props = {
  additionalContent?: ReactNode
  identifierFormat?: (typeof format)[keyof typeof format]
  identifierValue?: string
  identifierType?: IdentifierType | null
  onChange?: (value: string) => void
}

const IdentifierFormEnteringNumber = ({
  additionalContent,
  identifierFormat,
  identifierValue = '',
  identifierType,
  onChange,
}: Props) => {
  const { t } = useTranslation('identifiers-next')

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget
      if (onChange) {
        onChange(value)
      }
    },
    [onChange],
  )

  const props = {
    id: 'ident',
    onChange: handleChange,
    className: styles.input,
    value: identifierValue,
  }

  return (
    <>
      <div className={styles.root}>
        <div className={styles.inputContainer}>
          <PlainLabel htmlFor="ident" className={styles.inputLabel}>
            {identifierType === identifierTypes.IDENTIFIER_TINKOFF
              ? t('add_identifier_ID_input_label', 'Введите ID карты:')
              : t('add_identifier_input_label', 'Введите номер идентификатора:')}
          </PlainLabel>

          {identifierFormat === format.W26 && <PlainInputW26 {...props} />}
          {identifierFormat === format.W34 && <PlainInputW34 {...props} />}
          {identifierFormat === format.W58 && <PlainInputW58 {...props} />}

          {identifierFormat !== format.W26 &&
            identifierFormat !== format.W34 &&
            identifierFormat !== format.W58 && <PlainInput {...props} />}
        </div>

        {identifierType &&
          identifierType !== identifierTypes.IDENTIFIER_USER_PHOTO &&
          identifierType !== identifierTypes.IDENTIFIER_RFID_TAG &&
          identifierType !== identifierTypes.IDENTIFIER_TINKOFF && (
            <div className={styles.pictureContainer}>
              <PlainLabel htmlFor="ident" className={styles.inputLabel}>
                {t('add_identifier_example_picture_label', 'Узнать номер можно здесь:')}
              </PlainLabel>

              <IconByTypeWithMarks type={identifierType} />
            </div>
          )}

        {identifierType && identifierType === identifierTypes.IDENTIFIER_TINKOFF && (
          <PlainLabel className={styles.textLabel}>
            {t('add_identifier_ID_comment_label', 'ID выдается банком после выдачи карты')}
          </PlainLabel>
        )}
      </div>

      {identifierType && identifierType === identifierTypes.IDENTIFIER_TINKOFF && (
        <div className={styles.additionalTinkoffContainer}>
          <PlainLabel className={styles.additionalTinkoffTextLabel}>
            {t(
              'add_identifier_ID_comment_tinkoff',
              'Восьмизначный ID придет в чат поддержки Тинькофф, после выдачи карты. Добавление идентификатора возможно на следующий день после получения карты.',
            )}
          </PlainLabel>
          <PlainLink
            to="https://www.tinkoff.ru/cards/debit-cards/tinkoff-black/junior/school-card/?utm_source=innovat_jun&utm_medium=partners.unp&utm_campaign=school_0224_a13&utm_content=partner_web"
            external
          >
            {t('add_identifier_ID_comment_tinkoff_how_to_order', 'Как заказать карту?')}
          </PlainLink>

          {additionalContent}
        </div>
      )}

      {identifierType !== identifierTypes.IDENTIFIER_TINKOFF && additionalContent}
    </>
  )
}

export default IdentifierFormEnteringNumber
